import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@mui/styles";

const MIN_THUMB_HEIGHT = 20;

const styles = theme => ({
  box: {
    margin: "0 0 10px 0",
    padding: "12px",
    backgroundColor: "#FCF3D2",
    border: "1px solid #F5E3A3",
    borderRadius: "12px",
    textAlign: "left"
  },
  title: {
    margin: 0,
    fontSize: "12px",
    fontWeight: "700",
    lineHeight: 1.5,
    color: "#3D4051"
  },
  body: {
    marginTop: "12px",
    display: "flex",
    alignItems: "stretch"
  },
  list: {
    flex: 1,
    minWidth: 0,
    maxHeight: "112px",
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": {
      display: "none"
    }
  },
  item: {
    display: "block",
    width: "100%",
    margin: "0 0 6px 0",
    padding: "6px 10px",
    backgroundColor: "#FEF9E7",
    border: "1px solid #F3E3A6",
    borderRadius: "8px",
    boxShadow: "none",
    fontFamily: "inherit",
    fontSize: "12px",
    lineHeight: 1.4,
    color: "#3D4051",
    textAlign: "left",
    overflowWrap: "anywhere",
    cursor: "pointer",
    outline: "none",
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:last-child": {
      marginBottom: 0
    },
    "&:hover": {
      borderColor: "#FFD54F"
    },
    "&:focus-visible": {
      borderColor: "#FFC107"
    }
  },
  item_selected: {
    borderColor: "#FFC107",
    boxShadow: "inset 0 0 0 1px #FFC107",
    "&:hover": {
      borderColor: "#FFC107"
    }
  },
  track: {
    position: "relative",
    flex: "0 0 4px",
    width: "4px",
    marginLeft: "6px",
    backgroundColor: "#F7E9B5",
    borderRadius: "2px"
  },
  thumb: {
    position: "absolute",
    left: 0,
    width: "4px",
    backgroundColor: "#FFC107",
    borderRadius: "2px",
    cursor: "pointer",
    touchAction: "none"
  }
});

const sameEmail = (a, b) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

class CashboxApprovalEmails extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      scrollable: false,
      thumbHeight: 0,
      thumbTop: 0
    };
    this.listRef = React.createRef();
    this.drag = null;
  }

  componentDidMount() {
    this.measure();
    window.addEventListener("resize", this.measure);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.emails !== this.props.emails) {
      this.measure();
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.measure);
  }

  measure = () => {
    const list = this.listRef.current;

    if (!list) {
      return;
    }

    const { clientHeight, scrollHeight, scrollTop } = list;
    const scrollable = scrollHeight > clientHeight + 1;

    if (!scrollable) {
      this.setState({ scrollable: false, thumbHeight: 0, thumbTop: 0 });
      return;
    }

    const thumbHeight = Math.max(
      MIN_THUMB_HEIGHT,
      (clientHeight * clientHeight) / scrollHeight
    );
    const thumbTop =
      (scrollTop / (scrollHeight - clientHeight)) *
      (clientHeight - thumbHeight);

    this.setState({ scrollable: true, thumbHeight, thumbTop });
  };

  handleThumbPointerDown = event => {
    const list = this.listRef.current;

    if (!list) {
      return;
    }

    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    this.drag = { startY: event.clientY, startScrollTop: list.scrollTop };
  };

  handleThumbPointerMove = event => {
    const list = this.listRef.current;

    if (!this.drag || !list) {
      return;
    }

    const { clientHeight, scrollHeight } = list;
    const travel = clientHeight - this.state.thumbHeight;

    if (travel <= 0) {
      return;
    }

    list.scrollTop =
      this.drag.startScrollTop +
      ((event.clientY - this.drag.startY) * (scrollHeight - clientHeight)) /
        travel;
  };

  handleThumbPointerUp = event => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    this.drag = null;
  };

  render() {
    const { classes, emails, selectedEmail, onSelect } = this.props;
    const { scrollable, thumbHeight, thumbTop } = this.state;

    return (
      <div className={classes.box}>
        <p className={classes.title}>
          To approve the cashbox request, log in with one of these emails. Click
          one to fill it in.
        </p>
        <div className={classes.body}>
          <div
            ref={this.listRef}
            className={classes.list}
            onScroll={this.measure}
          >
            {emails.map(email => {
              const selected = sameEmail(email, selectedEmail);

              return (
                <button
                  key={email}
                  type="button"
                  aria-pressed={selected}
                  className={
                    classes.item + (selected ? " " + classes.item_selected : "")
                  }
                  onClick={() => onSelect(email)}
                >
                  {email}
                </button>
              );
            })}
          </div>
          {scrollable && (
            <div className={classes.track}>
              <div
                className={classes.thumb}
                style={{ height: thumbHeight, top: thumbTop }}
                onPointerDown={this.handleThumbPointerDown}
                onPointerMove={this.handleThumbPointerMove}
                onPointerUp={this.handleThumbPointerUp}
                onPointerCancel={this.handleThumbPointerUp}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}

CashboxApprovalEmails.propTypes = {
  classes: PropTypes.object.isRequired,
  emails: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedEmail: PropTypes.string,
  onSelect: PropTypes.func.isRequired
};

export default withStyles(styles)(CashboxApprovalEmails);
